/**
 * İmzacının AKTİF KİMLİĞİ (DEV-2603, DEV-3899/3901/3903).
 *
 * Tarayıcıdaki eİcraPro kimliği Electron proxy'sinin `/setConfig` ucu üzerinden bu
 * pencerenin sessionStorage'ına yazılır (token, tenant_id, tenant, tckn). Bu ham
 * değerler yalnız bir ÖNERİDİR: imzacı öneriyi sunucudaki lisansla doğrular
 * (GET /api/v1/imzaci/kimlik) ve doğrularsa AKTİF KİMLİK yapar. Bütün API istekleri
 * ve kimlik denetimleri aktif kimliği okur; bu yüzden tarayıcıda başka bir büroya ya
 * da hesaba geçmek çalışan botun kimliğini ortasında değiştiremez.
 *
 * Bu dosya saf tutuldu (store/DOM bağımlılığı yok, depo dışarıdan verilir) ki birim
 * testten geçebilsin. Karar tablosu `kimlikKarari`dır; "şimdi uygulanabilir mi" sorusu
 * `hemenUygulanabilirMi`dir.
 */
import {eicraproKimligiVarMi, KimlikRedSebebi} from '@/utils/eicraproBaglantisi';

/** Aktif kimliğin sessionStorage anahtarı (ham setConfig anahtarlarından AYRI). */
export const AKTIF_KIMLIK_ANAHTARI = 'imzaciAktifKimlik';

export interface KimlikTenant {
    tenant_id: string;
    title: string;
}

/**
 * TCKN'nin kaynağı: 'lisans' = GET /api/v1/imzaci/kimlik; 'setconfig' = eski backend
 * (endpoint yok, 404/405/501) için setConfig'teki ham TCKN.
 */
export type TcknKaynagi = 'lisans' | 'setconfig';

export interface AktifKimlik {
    /** Tam Authorization başlık değeri ('Bearer xxx'). */
    token: string;
    tenant: KimlikTenant;
    tenant_id: string;
    /** LİSANS sahibinin TCKN'si (11 hane); bütün kimlik denetimlerinde "hesap TCKN'si". */
    tckn: string;
    kaynak: TcknKaynagi;
}

export interface KimlikOnerisi {
    token: string;
    tenant: KimlikTenant;
    tenant_id: string;
    /** setConfig'teki HAM TCKN: 11 hane ya da ''. */
    tckn: string;
}

export interface LisansKimligi {
    lisansVar: boolean;
    /** 11 haneli lisans sahibi TCKN'si; yoksa null. */
    tckn: string | null;
    tcknEksik: boolean;
    lisansSahibi: string | null;
    lisansBitis: string | null;
    kaynak: TcknKaynagi;
}

export type KimlikKarari =
    | {tur: 'reddet', sebep: KimlikRedSebebi}
    | {tur: 'aktif-ol'}
    | {tur: 'degisiklik-yok'}
    | {tur: 'token-guncelle'}
    | {tur: 'buro-gec'}
    | {tur: 'tckn-degisti'};

const TCKN_DESENI = /^\d{11}$/;

function metin(deger: unknown): string {
    return typeof deger === 'string' ? deger : '';
}

function tokenGovdesi(token: string): string {
    return token.trim().replace(/^Bearer\s*/i, '');
}

/** 11 haneli TCKN ise kendisi, değilse ''. */
function gecerliTckn(deger: unknown): string {
    const s = typeof deger === 'number' ? String(deger) : metin(deger).trim();
    return TCKN_DESENI.test(s) ? s : '';
}

/**
 * sessionStorage'daki HAM öneriyi okur. Token ya da tenant_id yoksa/geçersizse null
 * ("Bearer undefined" gibi yarım devirler dahil). Tenant JSON'u bozuk ya da 'undefined'
 * ise başlık '' olur ama öneri geçerlidir; tam tenant nesnesi gelse bile yalnız
 * {tenant_id, title} alınır (üyelik/rol gibi alanlar taşınmaz).
 */
export function oneriyiOku(depo: Pick<Storage, 'getItem'> | null | undefined): KimlikOnerisi | null {
    if (!depo || !eicraproKimligiVarMi(depo)) {
        return null;
    }
    try {
        const token = String(depo.getItem('accessToken') ?? '').trim();
        const tenantId = String(depo.getItem('tenant_id') ?? '').trim();
        let baslik = '';
        try {
            const ham = depo.getItem('tenant');
            const nesne: unknown = ham ? JSON.parse(ham) : null;
            if (nesne && typeof nesne === 'object') {
                baslik = metin((nesne as {title?: unknown}).title).trim();
            }
        } catch (e) {
            baslik = '';
        }
        return {
            token,
            tenant: {tenant_id: tenantId, title: baslik},
            tenant_id: tenantId,
            tckn: gecerliTckn(depo.getItem('tckn'))
        };
    } catch (e) {
        return null;
    }
}

/**
 * Önerinin bellekte karşılaştırılan parmak izi (aynı öneri art arda gelirse sıfır istek).
 * Kalıcı yazılmaz, hash gerekmez; 'Bearer ' öneki sonucu değiştirmez.
 */
export function oneriParmakIzi(oneri: KimlikOnerisi): string {
    return JSON.stringify([tokenGovdesi(oneri.token), oneri.tenant_id, oneri.tckn]);
}

/**
 * GET /api/v1/imzaci/kimlik cevabını çözer. Düz gövde {lisans_var, ...} ve
 * {data: {...}} zarfı aynı sonucu verir (savunma). Tanınmayan gövde (nesne değil ya da
 * lisans_var yok) null döner: çağıran bunu "cevap alınamadı" sayar, kimlik ne reddedilir
 * ne terfi eder.
 */
export function lisansCevabiniCoz(govde: unknown): LisansKimligi | null {
    let kok: unknown = govde;
    if (kok && typeof kok === 'object' && !('lisans_var' in (kok as object)) && 'data' in (kok as object)) {
        kok = (kok as {data: unknown}).data;
    }
    if (!kok || typeof kok !== 'object') {
        return null;
    }
    const k = kok as {
        lisans_var?: unknown, lisans_bitis?: unknown, tckn?: unknown, lisans_sahibi?: unknown
    };
    if (typeof k.lisans_var !== 'boolean') {
        return null;
    }
    const tckn = gecerliTckn(k.tckn);
    const lisansVar = k.lisans_var === true;
    return {
        lisansVar,
        tckn: lisansVar && tckn ? tckn : null,
        tcknEksik: !(lisansVar && tckn),
        lisansSahibi: metin(k.lisans_sahibi).trim() || null,
        lisansBitis: metin(k.lisans_bitis).trim() || null,
        kaynak: 'lisans'
    };
}

/**
 * Eski backend (uç yok: 404/405/501) için yedek: setConfig'teki ham TCKN 11 haneyse
 * kaynak 'setconfig' ile tamam, boşsa TCKN eksik. Lisansın varlığı bilinemediği için
 * lisansVar true sayılır.
 */
export function yedekLisans(oneri: KimlikOnerisi): LisansKimligi {
    const tckn = oneri.tckn && TCKN_DESENI.test(oneri.tckn) ? oneri.tckn : '';
    return {
        lisansVar: true,
        tckn: tckn || null,
        tcknEksik: tckn === '',
        lisansSahibi: null,
        lisansBitis: null,
        kaynak: 'setconfig'
    };
}

/**
 * Öneri + lisans + mevcut aktif kimlikten karar:
 *  - lisans yok → reddet 'lisans-yok'; lisans sahibinin TCKN'si yok → reddet 'tckn-eksik'
 *    (aktif kimlik olsa da öneri reddedilir; aktif kimlik KORUNUR).
 *  - aktif yok → aktif-ol.
 *  - TCKN farklı (büro ne olursa olsun) → tckn-degisti (ASLA kendiliğinden: 'Geç').
 *  - TCKN aynı, büro farklı → buro-gec.
 *  - TCKN ve büro aynı: token aynıysa degisiklik-yok, değilse token-guncelle.
 */
export function kimlikKarari(
    aktif: AktifKimlik | null,
    oneri: KimlikOnerisi,
    lisans: LisansKimligi
): KimlikKarari {
    if (!lisans.lisansVar) {
        return {tur: 'reddet', sebep: 'lisans-yok'};
    }
    if (!lisans.tckn) {
        return {tur: 'reddet', sebep: 'tckn-eksik'};
    }
    if (!aktif) {
        return {tur: 'aktif-ol'};
    }
    if (aktif.tckn !== lisans.tckn) {
        return {tur: 'tckn-degisti'};
    }
    if (aktif.tenant_id !== oneri.tenant_id) {
        return {tur: 'buro-gec'};
    }
    return tokenGovdesi(aktif.token) === tokenGovdesi(oneri.token)
        ? {tur: 'degisiklik-yok'}
        : {tur: 'token-guncelle'};
}

/**
 * Karar şimdi uygulanabilir mi (güvenli nokta kuralı):
 *  - aktif-ol her zaman (henüz çalışan bir kimlik yok);
 *  - token-guncelle bot boştayken; aktif kimliğin token'ı zaten geçersizse (401) hemen;
 *  - buro-gec bot boşta VE kuyruk boşken; aktif kimliğin token'ı zaten geçersizse (401) kuyruk
 *    boşluğu beklenmez: eski büronun görevleri o token'la ilerleyemez, korunacak bir şey yok ve
 *    bekleme (EICRAPRO_BEKLENIYOR) "bot kendiliğinden devam eder" vaadini tutmalı. kuyrukBos'u
 *    token geçersizliğiyle yeniden tanımlamayın: canlı token'da zamanlanmış görev koruması bozulur;
 *  - tckn-degisti HİÇBİR koşulda (yalnız kullanıcının 'Geç'i).
 */
export function hemenUygulanabilirMi(
    karar: KimlikKarari,
    durum: {botBosta: boolean, kuyrukBos: boolean, tokenGecersiz?: boolean}
): boolean {
    switch (karar.tur) {
        case 'aktif-ol':
            return true;
        case 'token-guncelle':
            return durum.botBosta || durum.tokenGecersiz === true;
        case 'buro-gec':
            return durum.botBosta && (durum.kuyrukBos || durum.tokenGecersiz === true);
        default:
            return false;
    }
}

/** Öneri + lisanstan aktif kimlik (TCKN yoksa null). */
export function aktifKimligeDonustur(oneri: KimlikOnerisi, lisans: LisansKimligi): AktifKimlik | null {
    if (!lisans.lisansVar || !lisans.tckn) {
        return null;
    }
    return {
        token: oneri.token,
        tenant: {tenant_id: oneri.tenant_id, title: oneri.tenant.title},
        tenant_id: oneri.tenant_id,
        tckn: lisans.tckn,
        kaynak: lisans.kaynak
    };
}

/** sessionStorage'dan geri yüklemede şekil ve 11 hane denetimi; bozuksa null. */
export function aktifKimligiDogrula(ham: unknown): AktifKimlik | null {
    if (!ham || typeof ham !== 'object') {
        return null;
    }
    const k = ham as {
        token?: unknown, tenant?: unknown, tenant_id?: unknown, tckn?: unknown, kaynak?: unknown
    };
    const token = metin(k.token).trim();
    const tenantId = metin(k.tenant_id).trim();
    const tckn = metin(k.tckn);
    const govde = tokenGovdesi(token);
    if (govde === '' || govde === 'undefined' || govde === 'null' || tenantId === '' || !TCKN_DESENI.test(tckn)) {
        return null;
    }
    if (k.kaynak !== 'lisans' && k.kaynak !== 'setconfig') {
        return null;
    }
    let baslik = '';
    if (k.tenant && typeof k.tenant === 'object') {
        baslik = metin((k.tenant as {title?: unknown}).title).trim();
    }
    return {
        token,
        tenant: {tenant_id: tenantId, title: baslik},
        tenant_id: tenantId,
        tckn,
        kaynak: k.kaynak
    };
}

/**
 * Socket'e yayınlanan `botKimligi` yükü (TCKN İÇERMEZ). Aktif kimlik yoksa tenant_id ''
 * yayınlanır: tarayıcı bunu "bot kimliği yok" sayar.
 */
export function botKimligiYuku(
    aktif: AktifKimlik | null,
    bekleyenGecis: boolean,
    avukat: string
): {tenant_id: string, buro: string, avukat: string, bekleyenGecis: boolean} {
    if (!aktif) {
        return {tenant_id: '', buro: '', avukat: '', bekleyenGecis: false};
    }
    return {
        tenant_id: aktif.tenant_id,
        buro: aktif.tenant.title,
        avukat: avukat,
        bekleyenGecis: bekleyenGecis === true
    };
}
